import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  genders,
  associateSchools,
  academicStatuses,
  professionalStatuses,
  getYearOptions,
} from "../../../utils/options";
import "./PendingAlumniPage.css";
import { Printer, FileSpreadsheet } from "lucide-react";
import * as XLSX from "xlsx";
import { printTableWithHeader } from "../../../utils/printUtils";

const PAGE_SIZE = 30;
const API_BASE_URL = process.env.REACT_APP_BACKEND_API_URL;

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });
};

const formatTime = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

export default function PendingAlumniPage() {
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    gender: "",
    year: "",
    associateSchool: "",
    academicStatus: "",
    professionalStatus: "",
  });
  const [pendingAlumni, setPendingAlumni] = useState([]);
  const [serverTime, setServerTime] = useState({ fullDateTime: "" });
  const [currentUser, setCurrentUser] = useState({ fullname: "", systemRole: "" });

  // Load current user
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) setCurrentUser({ fullname: user.fullname, systemRole: user.systemRole });
  }, []);

  // Fetch pending alumni and server time
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [alumniRes, timeRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/registrations/pending`),
          axios.get(`${API_BASE_URL}/time/datetime`),
        ]);

        setPendingAlumni(alumniRes.data.data || []);
        if (timeRes.data.success) setServerTime(timeRes.data);
      } catch (err) {
        console.error("Failed to fetch data:", err.message);
      } finally {
        setTimeout(() => setLoading(false), 1200);
      }
    };
    fetchData();
  }, []);

  // Compute filtered and searchable alumni
  const filteredAlumni = useMemo(() => {
    return pendingAlumni
      .map((a) => ({
        ...a,
        fullName: [a.title, a.firstName, a.middleName, a.lastName].filter(Boolean).join(" "),
      }))
      .filter((a) => {
        const matchesSearch =
          a.alumniID.toLowerCase().includes(search.toLowerCase()) ||
          a.email?.toLowerCase().includes(search.toLowerCase()) ||
          a.fullName.toLowerCase().includes(search.toLowerCase());

        const matchesFilters =
          (!filters.gender || a.gender === filters.gender) &&
          (!filters.year || a.yearOfGraduation === Number(filters.year)) &&
          (!filters.associateSchool || a.associateSchool === filters.associateSchool) &&
          (!filters.academicStatus || a.academicStatus === filters.academicStatus) &&
          (!filters.professionalStatus || a.professionalStatus === filters.professionalStatus);

        return matchesSearch && matchesFilters;
      });
  }, [pendingAlumni, search, filters]);

  const totalPages = Math.ceil(filteredAlumni.length / PAGE_SIZE);
  const paginatedData = filteredAlumni.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Update registration status
  const handleStatusUpdate = async (alumniId, newStatus) => {
    if (!window.confirm(`Are you sure you want to mark this registration as ${newStatus}?`)) return;

    try {
      await axios.put(`${API_BASE_URL}/registrations/${alumniId}/status`, {
        registrationStatus: newStatus,
        approvedBy: currentUser.fullname,
      });
      setPendingAlumni((prev) =>
        prev.map((a) =>
          a._id === alumniId ? { ...a, registrationStatus: newStatus } : a
        )
      );
      alert(`Alumni registration has been ${newStatus} successfully.`);
    } catch (err) {
      console.error("Failed to update registration status:", err.message);
      alert("Failed to update registration status.");
    }
  };

  // Print table
  const handlePrint = () => {
    printTableWithHeader({
      title: "Pending Alumni List",
      summary: "List of all pending alumni registrations",
      columns: [
        { key: "sn", label: "SN" },
        { key: "alumniID", label: "Alumni ID" },
        { key: "fullName", label: "Full Name" },
        { key: "gender", label: "Gender" },
        { key: "phone", label: "Telephone" },
        { key: "status", label: "Status" },
      ],
      rows: filteredAlumni.map((a, idx) => ({
        sn: idx + 1,
        alumniID: a.alumniID,
        fullName: a.fullName,
        gender: a.gender,
        phone: a.mobileNumbers?.[0] ? `+${a.mobileNumbers[0]}` : "",
        status: a.registrationStatus,
      })),
    });
  };

  // Export to Excel
  const handleExportExcel = () => {
    const dataForExcel = filteredAlumni.map((a, idx) => ({
      SN: idx + 1,
      "Alumni ID": a.alumniID,
      "Full Name": a.fullName,
      Gender: a.gender,
      Telephone: a.mobileNumbers?.[0] ? `+${a.mobileNumbers[0]}` : "",
      Status: a.registrationStatus,
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataForExcel);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "PendingAlumni");
    XLSX.writeFile(workbook, "pending_alumni.xlsx");
  };

  return (
    <div className="pending-alumni-page">
      {/* USER INFO */}
      <div className="pending-user-card">
        <div className="pending-user-left">
          <div className="pending-user-name">Welcome, {currentUser.fullname}</div>
          <div className="pending-user-role">System Role: {currentUser.systemRole}</div>
        </div>
        <div className="pending-user-right">
          <div className="pending-user-date">{formatDate(serverTime.fullDateTime)}</div>
          <div className="pending-user-time">{formatTime(serverTime.fullDateTime)}</div>
        </div>
      </div>

      <h1 className="pending-page-title">Alumni Pending Approvals</h1>

      <div className="pending-action-row">
        <button className="pending-btn-primary" onClick={handlePrint}><Printer size={16} /> Print</button>
        <button className="pending-btn-secondary" onClick={handleExportExcel}><FileSpreadsheet size={16} /> Export</button>
      </div>

      {/* FILTERS */}
      <div className="pending-filters">
        <select onChange={(e) => setFilters({ ...filters, gender: e.target.value })}>
          <option value="">Gender</option>
          {genders.map((g) => <option key={g} value={g}>{g}</option>)}
        </select>

        <select onChange={(e) => setFilters({ ...filters, year: e.target.value })}>
          <option value="">Year of Graduation</option>
          {getYearOptions(50).map((y) => <option key={y} value={y}>{y}</option>)}
        </select>

        <select onChange={(e) => setFilters({ ...filters, associateSchool: e.target.value })}>
          <option value="">Associate School</option>
          {associateSchools.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>

        <select onChange={(e) => setFilters({ ...filters, academicStatus: e.target.value })}>
          <option value="">Academic Status</option>
          {academicStatuses.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>

        <select onChange={(e) => setFilters({ ...filters, professionalStatus: e.target.value })}>
          <option value="">Professional Status</option>
          {professionalStatuses.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <input
        className="pending-search-input"
        placeholder="Search by Alumni ID, Full Name or Email"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {/* TABLE */}
      <div className="pending-table-card">
        <div className="pending-table-meta">
          <span>Total Pending: {filteredAlumni.length}</span>
          <span>Page {page} of {totalPages}</span>
        </div>

        {loading ? (
          <div className="pending-loading">Loading pending alumni…</div>
        ) : (
          <table className="pending-table">
            <thead>
              <tr>
                <th>SN</th>
                <th>Alumni ID</th>
                <th>Full Name</th>
                <th>Gender</th>
                <th>Telephone</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((a, idx) => (
                <tr key={a._id}>
                  <td>{(page - 1) * PAGE_SIZE + idx + 1}</td>
                  <td>{a.alumniID}</td>
                  <td>{a.fullName}</td>
                  <td>{a.gender}</td>
                  <td>{a.mobileNumbers?.[0] ? `+${a.mobileNumbers[0]}` : ""}</td>
                  <td className="pending-approve-cell">
                    <select
                      value={a.registrationStatus}
                      onChange={(e) => handleStatusUpdate(a._id, e.target.value)}
                    >
                      <option value="PENDING">PENDING</option>
                      <option value="APPROVED">APPROVED</option>
                      <option value="REJECTED">REJECTED</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* PAGINATION */}
        <div className="pending-pagination">
          <button disabled={page === 1} onClick={() => setPage(page - 1)}>Previous</button>
          <button disabled={page === totalPages} onClick={() => setPage(page + 1)}>Next</button>
        </div>
      </div>
    </div>
  );
}